import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';

export default function SupportSowsPage() {
  const [sows, setSows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { loadSows(); }, []);

  async function loadSows() {
    setLoading(true);
    try {
      const data = await api.get('support-sows');
      setSows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.status === 403
        ? 'Your role does not have the Support SOWs page.'
        : 'Failed to load support SOWs. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function loadSummary(sowId) {
    setSummaryLoading(true);
    setSummary(null);
    try {
      const data = await api.get(`support-sows/${sowId}/summary`);
      setSummary(data);
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }

  function selectSow(sow) {
    setSelected(sow);
    setActiveTab('overview');
    loadSummary(sow.id);
  }

  function pctColourText(pct) {
    if (pct >= 100) return 'text-red-700';
    if (pct >= 80) return 'text-orange-600';
    return 'text-green-700';
  }

  function pctColourBg(pct) {
    if (pct >= 100) return 'bg-red-500';
    if (pct >= 80) return 'bg-orange-400';
    return 'bg-green-500';
  }

  function pctBadge(pct) {
    if (pct >= 100) return 'bg-red-100 text-red-700';
    if (pct >= 80) return 'bg-orange-100 text-orange-700';
    return 'bg-green-100 text-green-700';
  }

  if (loading) {
    return <div className="p-8 text-center text-gray-400 text-sm">Loading support SOWs…</div>;
  }
  if (error) {
    return <div className="p-8 text-center text-red-500 text-sm">{error}</div>;
  }

  return (
    <div className="flex h-full min-h-0">
      {/* SOW list panel */}
      <div className="w-64 lg:w-80 border-r border-gray-200 bg-white flex flex-col flex-shrink-0 overflow-hidden">
        <div className="p-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <h2 className="text-sm font-semibold text-gray-800">Support SOWs</h2>
          <p className="text-xs text-gray-500 mt-0.5">{sows.length} active contract{sows.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {sows.length === 0 ? (
            <div className="p-4 text-sm text-gray-500">
              No active support SOWs. Open a SOW in the tracker and enable the support contract flag.
            </div>
          ) : (
            <ul>
              {sows.map(sow => {
                const pct = sow.support_hours_budget > 0
                  ? Math.min(100, Math.round((sow.hours_used / sow.support_hours_budget) * 100))
                  : 0;
                const isActive = selected?.id === sow.id;
                return (
                  <li
                    key={sow.id}
                    onClick={() => selectSow(sow)}
                    className={`p-3 border-b border-gray-100 cursor-pointer transition-colors hover:bg-blue-50 ${isActive ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <span className="font-mono text-xs text-blue-700">{sow.reference}</span>
                      <span className={`text-xs px-1.5 rounded-full font-medium ${pctBadge(pct)}`}>{pct}%</span>
                    </div>
                    <div className="text-sm font-medium text-gray-800 truncate mt-0.5">{sow.client_name || '—'}</div>
                    <div className="text-xs text-gray-500 truncate">{sow.detail}</div>
                    {sow.support_hours_budget > 0 && (
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>{Number(sow.hours_used).toFixed(1)}h used</span>
                          <span>{sow.support_hours_budget}h budget</span>
                        </div>
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${pctColourBg(pct)}`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Detail panel */}
      {summaryLoading ? (
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Loading…</div>
      ) : summary ? (
        <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-white overflow-hidden">
          {/* Header */}
          <div className="p-5 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="font-mono text-xs text-gray-400">{summary.sow.reference}</span>
                <h1 className="text-lg font-semibold text-gray-900 mt-0.5">{summary.sow.client_name}</h1>
                <p className="text-sm text-gray-500">{summary.sow.detail}</p>
              </div>
              <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 flex-shrink-0">{summary.sow.status}</span>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {[
                { label: 'Hours Budget', value: `${summary.hours.budget}h`, colour: 'text-gray-800' },
                {
                  label: `Hours Used (${summary.hours.pct_used}%)`,
                  value: `${Number(summary.hours.used).toFixed(1)}h`,
                  colour: pctColourText(summary.hours.pct_used),
                },
                {
                  label: 'Remaining',
                  value: `${Number(summary.hours.remaining).toFixed(1)}h`,
                  colour: summary.hours.remaining === 0 ? 'text-red-700' : 'text-gray-800',
                },
                { label: 'Linked Tickets', value: summary.incidents.total_linked, colour: 'text-gray-800' },
              ].map(kpi => (
                <div key={kpi.label} className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className={`text-2xl font-bold ${kpi.colour}`}>{kpi.value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{kpi.label}</div>
                </div>
              ))}
            </div>

            {summary.hours.budget > 0 && (
              <div className="mt-3">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${pctColourBg(summary.hours.pct_used)}`}
                    style={{ width: `${Math.min(100, summary.hours.pct_used)}%` }}
                  />
                </div>
                {summary.hours.pct_used >= 100 && (
                  <p className="text-xs text-red-600 mt-1">
                    Hours budget exceeded by {(summary.hours.used - summary.hours.budget).toFixed(1)}h
                  </p>
                )}
                {summary.hours.pct_used >= 80 && summary.hours.pct_used < 100 && (
                  <p className="text-xs text-orange-600 mt-1">
                    Approaching limit — {summary.hours.remaining.toFixed(1)}h remaining
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 px-5 bg-white flex-shrink-0">
            {[['overview', 'By Engineer'], ['incidents', 'Incidents'], ['time', 'Time Log']].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto p-5">
            {activeTab === 'overview' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Time logged by engineer</h3>
                {summary.time_by_person.length === 0 ? (
                  <p className="text-sm text-gray-400">No time logged against this SOW yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 border-b border-gray-100">
                        <th className="text-left pb-2 font-medium">Engineer</th>
                        <th className="text-right pb-2 font-medium">Hours</th>
                        <th className="text-right pb-2 font-medium">Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.time_by_person.map((p, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-2">{p.display_name}</td>
                          <td className="py-2 text-right font-mono">{Number(p.total_hours).toFixed(1)}h</td>
                          <td className="py-2 text-right text-gray-400">
                            {summary.hours.used > 0 ? Math.round((p.total_hours / summary.hours.used) * 100) : 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'incidents' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Linked incidents & requests</h3>
                {summary.incidents.links.length === 0 ? (
                  <p className="text-sm text-gray-400">No tickets linked yet. Link from the incident management system.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 border-b border-gray-100">
                        <th className="text-left pb-2 font-medium">Reference</th>
                        <th className="text-left pb-2 font-medium">Title</th>
                        <th className="text-left pb-2 font-medium">Kind</th>
                        <th className="text-right pb-2 font-medium">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const withTime = summary.time_by_incident;
                        const noTime = summary.incidents.links.filter(
                          l => !withTime.find(t => t.incident_reference === l.incident_reference)
                        );
                        return [...withTime, ...noTime.map(l => ({
                          incident_reference: l.incident_reference,
                          incident_kind: l.incident_kind,
                          title: l.incident_title || l.incident_reference,
                          total_hours: 0,
                          entry_count: 0,
                        }))].map((inc, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="py-2 font-mono text-xs text-blue-700">{inc.incident_reference}</td>
                            <td className="py-2 text-gray-700 truncate max-w-xs">{inc.title}</td>
                            <td className="py-2">
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${inc.incident_kind === 'request' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {inc.incident_kind || 'incident'}
                              </span>
                            </td>
                            <td className="py-2 text-right font-mono">{Number(inc.total_hours).toFixed(1)}h</td>
                          </tr>
                        ));
                      })()} 
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {activeTab === 'time' && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Time breakdown by ticket</h3>
                {summary.time_by_incident.length === 0 ? (
                  <p className="text-sm text-gray-400">No time entries yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 border-b border-gray-100">
                        <th className="text-left pb-2 font-medium">Ticket</th>
                        <th className="text-right pb-2 font-medium">Hours</th>
                        <th className="text-right pb-2 font-medium">Entries</th>
                        <th className="text-right pb-2 font-medium">% of total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.time_by_incident.map((inc, i) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="py-2">
                            <div className="font-mono text-xs text-blue-700">{inc.incident_reference}</div>
                            <div className="text-xs text-gray-400 truncate">{inc.title}</div>
                          </td>
                          <td className="py-2 text-right font-mono">{Number(inc.total_hours).toFixed(1)}h</td>
                          <td className="py-2 text-right text-gray-400">{inc.entry_count}</td>
                          <td className="py-2 text-right text-gray-400">
                            {summary.hours.used > 0 ? Math.round((inc.total_hours / summary.hours.used) * 100) : 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center text-gray-400">
            <div className="text-5xl mb-3">📋</div>
            <div className="text-sm">Select a support SOW to view profitability</div>
          </div>
        </div>
      )}
    </div>
  );
}
